import { GRID_COLS, GRID_MAX_ROWS } from '../constants';
import { GridLayoutItem } from '../types';

export interface GridRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Creates a 2D boolean grid representing occupied cells.
 * true = occupied, false = empty
 * @param excludeItemId - Optional item ID to exclude from occupancy (for move operations)
 */
export function createOccupancyGrid(items: GridLayoutItem[], excludeItemId?: string): boolean[][] {
  // Initialize grid with all false (empty)
  const grid: boolean[][] = Array.from({ length: GRID_MAX_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => false)
  );

  // Mark occupied cells (excluding specified item)
  for (const item of items) {
    if (excludeItemId && item.i === excludeItemId) {
      continue;
    }
    for (let row = item.y; row < item.y + item.h && row < GRID_MAX_ROWS; row++) {
      for (let col = item.x; col < item.x + item.w && col < GRID_COLS; col++) {
        grid[row][col] = true;
      }
    }
  }

  return grid;
}

/**
 * Check if a single cell is occupied
 */
export function isCellOccupied(grid: boolean[][], x: number, y: number): boolean {
  if (y < 0 || y >= GRID_MAX_ROWS || x < 0 || x >= GRID_COLS) {
    return true; // Out of bounds = occupied
  }
  return grid[y][x];
}

/**
 * Check if a rectangle area is fully unoccupied
 */
export function isRectAvailable(grid: boolean[][], rect: GridRect): boolean {
  // Check bounds
  if (rect.x < 0 || rect.y < 0) {
    return false;
  }
  if (rect.x + rect.w > GRID_COLS) {
    return false;
  }
  if (rect.y + rect.h > GRID_MAX_ROWS) {
    return false;
  }

  // Check each cell
  for (let row = rect.y; row < rect.y + rect.h; row++) {
    for (let col = rect.x; col < rect.x + rect.w; col++) {
      if (grid[row][col]) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Find the largest blank rectangle starting from a given position.
 * Uses a greedy approach: expand right, then down, as far as possible.
 */
export function findLargestBlankRect(grid: boolean[][], startX: number, startY: number): GridRect | null {
  // Clamp start position to grid bounds
  if (startX < 0) {
    startX = 0;
  }
  if (startY < 0) {
    startY = 0;
  }
  if (startX >= GRID_COLS || startY >= GRID_MAX_ROWS) {
    return null;
  }

  // If starting cell is occupied, no space available here
  if (grid[startY][startX]) {
    return null;
  }

  // Find max width from start position (how far right can we go?)
  let maxWidth = 0;
  for (let col = startX; col < GRID_COLS; col++) {
    if (grid[startY][col]) {
      break;
    }
    maxWidth++;
  }

  if (maxWidth === 0) {
    return null;
  }

  // Find max height while maintaining full width
  let maxHeight = 0;
  let currentWidth = maxWidth;

  for (let row = startY; row < GRID_MAX_ROWS; row++) {
    // Check how far right we can go on this row
    let rowWidth = 0;
    for (let col = startX; col < startX + currentWidth; col++) {
      if (grid[row][col]) {
        break;
      }
      rowWidth++;
    }

    if (rowWidth === 0) {
      break;
    }

    // Narrow the width if this row is narrower
    currentWidth = Math.min(currentWidth, rowWidth);
    maxHeight++;
  }

  if (maxHeight === 0) {
    return null;
  }

  return {
    x: startX,
    y: startY,
    w: currentWidth,
    h: maxHeight,
  };
}

/**
 * Find a valid drop rectangle for a panel at the specified position.
 * @param allowShrink - If true, allows returning a smaller rect if default size doesn't fit.
 *                      If false, returns null unless the full size fits.
 */
export function findValidDropRect(
  grid: boolean[][],
  dropX: number,
  dropY: number,
  defaultW: number,
  defaultH: number,
  allowShrink = false
): GridRect | null {
  // Clamp drop position to valid range
  const x = Math.max(0, Math.min(dropX, GRID_COLS - 1));
  const y = Math.max(0, Math.min(dropY, GRID_MAX_ROWS - 1));

  // First, try the full size
  const fullRect: GridRect = { x, y, w: defaultW, h: defaultH };
  if (isRectAvailable(grid, fullRect)) {
    return fullRect;
  }

  // If shrinking is not allowed, return null
  if (!allowShrink) {
    return null;
  }

  // Find available space starting from drop position
  const blankRect = findLargestBlankRect(grid, x, y);
  if (!blankRect) {
    return null;
  }

  // Fit within the available space
  const fitW = Math.min(defaultW, blankRect.w, GRID_COLS - x);
  const fitH = Math.min(defaultH, blankRect.h, GRID_MAX_ROWS - y);

  if (fitW < 1 || fitH < 1) {
    return null;
  }

  return { x, y, w: fitW, h: fitH };
}

/**
 * Calculate grid position from pixel coordinates
 */
export function pixelToGrid(
  pixelX: number,
  pixelY: number,
  gridWidth: number,
  rowHeight: number
): { x: number; y: number } {
  const cellWidth = gridWidth / GRID_COLS;
  return {
    x: Math.floor(pixelX / cellWidth),
    y: Math.floor(pixelY / rowHeight),
  };
}
